import React, { useEffect, useRef, useState } from 'react';
import { Modal, Button, Form, Alert, Spinner, Row, Col } from 'react-bootstrap';
import api from '../api';

// Richiesta fattura da un messaggio IBS: propone i dati letti dal modulo "Dati di fatturazione" del cliente,
// li controlla, emette la fattura elettronica con Fattura24 e prepara la risposta, che parte all'esito SDI.
const CAMPI = [
  { key: 'denominazione', label: 'Intestatario (ragione sociale)', placeholder: 'vuoto = nome e cognome del cliente', md: 12 },
  { key: 'indirizzo', label: 'Indirizzo', md: 7 },
  { key: 'numero_civico', label: 'Civico', md: 2 },
  { key: 'cap', label: 'CAP', md: 3 },
  { key: 'citta', label: 'Città', md: 8 },
  { key: 'provincia', label: 'Provincia', placeholder: 'sigla', md: 4 },
  { key: 'codice_fiscale', label: 'Codice fiscale', placeholder: '16 caratteri o 11 cifre (ente)', md: 6 },
  { key: 'partita_iva', label: 'Partita IVA (se azienda)', placeholder: '11 cifre', md: 6 },
  { key: 'codice_destinatario', label: 'Codice destinatario SDI (opzionale)', placeholder: '7 caratteri', md: 6 },
  { key: 'pec', label: 'PEC (opzionale)', md: 6 },
];
const FISCALI = new Set(['denominazione', 'partita_iva', 'codice_fiscale', 'codice_destinatario', 'pec']);
const vuoti = Object.fromEntries(CAMPI.map(c => [c.key, '']));

export default function FatturaDaMessaggioModal({ show, onHide, ordineId, threadId, onDone }) {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);
  const [dati, setDati] = useState(vuoti);
  const [aggiornaAnagrafica, setAggiornaAnagrafica] = useState(true);
  const [validazione, setValidazione] = useState({ errori: [], avvisi: [] });
  const [avvisiDaConfermare, setAvvisiDaConfermare] = useState(false);
  const [messaggio, setMessaggio] = useState('');
  const timer = useRef(null);

  useEffect(() => {
    if (!show || !ordineId) return;
    setLoading(true);
    setError(null);
    api.get(`/mirakl/richieste-fattura/anteprima/${ordineId}`)
      .then(res => {
        const d = res.data;
        setInfo(d);
        const pick = (k) => {
          const proposto = d.dati_proposti?.[k];
          const anagrafica = d.dati_fiscali?.[k];
          // Il modulo Mirakl e' quanto chiesto dal cliente: vince sull'anagrafica, anche quando un campo e' vuoto
          if (d.da_modulo_mirakl) return proposto ?? (FISCALI.has(k) ? '' : anagrafica) ?? '';
          return anagrafica || proposto || '';
        };
        setDati(Object.fromEntries(CAMPI.map(c => [c.key, pick(c.key)])));
        setAggiornaAnagrafica(!d.intestata_a_terzi);
        setMessaggio(d.messaggio);
      })
      .catch(e => setError(e.response?.data?.detail || 'Impossibile caricare i dati dell\'ordine.'))
      .finally(() => setLoading(false));
  }, [show, ordineId]);

  const soloMessaggio = info?.fattura_inviata;

  useEffect(() => {
    if (!info || soloMessaggio) return;
    clearTimeout(timer.current);
    setAvvisiDaConfermare(false);
    timer.current = setTimeout(() => {
      api.post('/mirakl/richieste-fattura/valida', { ordine_id: ordineId, dati })
        .then(res => setValidazione(res.data))
        .catch(() => {});
    }, 400);
    return () => clearTimeout(timer.current);
  }, [dati, info, soloMessaggio, ordineId]);

  const letto = (k) => !!info?.dati_proposti?.[k] && info.dati_proposti[k] !== info.dati_fiscali?.[k];

  const invia = async (forza) => {
    setSending(true);
    setError(null);
    try {
      const res = await api.post('/mirakl/richieste-fattura/emetti', {
        ordine_id: ordineId, thread_id: threadId, messaggio, ...dati, aggiorna_anagrafica: aggiornaAnagrafica, forza,
      });
      onDone && onDone(res.data);
      onHide();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.avvisi) {
        setValidazione(d);
        setAvvisiDaConfermare(true);
      } else if (d?.errori) {
        setValidazione(d);
      } else {
        setError(typeof d === 'string' ? d : 'Operazione non riuscita.');
      }
    } finally {
      setSending(false);
    }
  };

  const bloccato = !soloMessaggio && validazione.errori?.length > 0;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton><Modal.Title className="fs-5">Richiesta fattura</Modal.Title></Modal.Header>
      <Modal.Body>
        {loading ? <div className="text-center"><Spinner animation="border" /></div> : info && (
          <>
            <div className="mb-3 small">
              Ordine <strong>{info.mirakl_order_id || info.ordine_id}</strong> — {info.cliente || 'cliente'} —{' '}
              {info.importo_ordine != null ? Number(info.importo_ordine).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }) : ''}
            </div>
            {soloMessaggio ? (
              <Alert variant="info">
                La fattura{info.fattura_numero ? ` n. ${info.fattura_numero}` : ''} è già stata emessa (stato SDI: {info.fattura_sdi_stato}).
                Non verrà creata di nuovo: si prepara solo il messaggio al cliente.
                {info.messaggio_in_attesa && ' Un messaggio è già in attesa dell\'esito SDI.'}
              </Alert>
            ) : (
              <>
                {info.ha_ricevuta && <Alert variant="warning" className="py-2 small">Esiste una ricevuta per questo ordine: verrà annullata (con storno dei corrispettivi) e sostituita dalla fattura.</Alert>}
                {info.intestata_a_terzi && (
                  <Alert variant="info" className="py-2 small">
                    Il cliente chiede la fattura intestata a <strong>{info.dati_proposti.denominazione}</strong>, diverso da {info.cliente}:
                    i dati valgono solo per questa fattura e la scheda cliente non viene toccata.
                  </Alert>
                )}
                <Row>
                  {CAMPI.map(({ key, label, placeholder, md }) => (
                    <Col md={md} className="mb-2" key={key}>
                      <Form.Label className="small mb-1">{label} {letto(key) && <span className="text-success">(dal messaggio)</span>}</Form.Label>
                      <Form.Control size="sm" value={dati[key] ?? ''} placeholder={placeholder}
                        onChange={(e) => setDati({ ...dati, [key]: e.target.value })} />
                    </Col>
                  ))}
                </Row>
                <Form.Check type="checkbox" id="fattura-msg-aggiorna" className="small mb-2"
                  checked={aggiornaAnagrafica} onChange={e => setAggiornaAnagrafica(e.target.checked)}
                  label="Salva ragione sociale, P.IVA, codice fiscale, codice destinatario e PEC nella scheda cliente (l'indirizzo resta quello di spedizione)" />
                {validazione.errori?.length > 0 && (
                  <Alert variant="danger" className="py-1 px-2 small mb-2">
                    <ul className="mb-0 ps-3">{validazione.errori.map((m, i) => <li key={i}>{m}</li>)}</ul>
                  </Alert>
                )}
                {validazione.avvisi?.length > 0 && (
                  <Alert variant="warning" className="py-1 px-2 small mb-2">
                    <ul className="mb-0 ps-3">{validazione.avvisi.map((m, i) => <li key={i}>{m}</li>)}</ul>
                  </Alert>
                )}
                <div className="text-muted small mb-3">Verifica i dati: una volta inviata, la fattura va al Sistema di Interscambio e non si richiama.</div>
              </>
            )}
            <Form.Label className="small mb-1">Messaggio al cliente</Form.Label>
            <Form.Control as="textarea" rows={6} value={messaggio} onChange={(e) => setMessaggio(e.target.value)} />
            <div className="text-muted small mt-1">Parte quando lo SDI conferma la consegna, così non si annuncia una fattura poi scartata.</div>
          </>
        )}
        {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="light" onClick={onHide} disabled={sending}>Annulla</Button>
        <Button variant={avvisiDaConfermare ? 'danger' : 'primary'} onClick={() => invia(avvisiDaConfermare)}
          disabled={loading || sending || !info || bloccato || !messaggio.trim()}>
          {sending ? <Spinner as="span" animation="border" size="sm" />
            : soloMessaggio ? 'Invia messaggio al cliente'
              : avvisiDaConfermare ? 'Emetti comunque' : 'Emetti fattura'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
